import {
  CheckCircle,
  Clear,
  Person,
  PersonAdd,
  Search,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import {
  EnumUserRelationshipsType,
  useSearchCustomersLazyQuery,
} from 'lib/graphql';

import { CustomerResult } from '../../../shared/split-slot/types';
import { SplitSlotDrawer } from '../../../shared/split-slot/split-slot-drawer';
import { formatPhoneDisplay } from 'features/customers/helpers/formatting';

interface ExistingBuyerDrawerProps {
  open: boolean;
  vendorId: string;
  producerId?: string;
  relationshipType?: EnumUserRelationshipsType;
  title?: string;
  myselfLabel?: string;
  onSelect: (userId: string) => void;
  onSelectMyself: () => void;
  onOpenNewBuyer: () => void;
  onClose: () => void;
}

export function ExistingBuyerDrawer({
  open,
  vendorId,
  producerId,
  relationshipType,
  title = 'Select Existing Buyer',
  myselfLabel = 'Myself',
  onSelect,
  onSelectMyself,
  onOpenNewBuyer,
  onClose,
}: ExistingBuyerDrawerProps) {
  const [selectedUser, setSelectedUser] = useState<CustomerResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCustomers, { data, loading: searching }] =
    useSearchCustomersLazyQuery();

  const debouncedSearch = useRef(
    debounce((term: string) => {
      setHasSearched(true);
      searchCustomers({
        variables: {
          vendorId,
          ...(producerId && { producerId }),
          ...(relationshipType && { relationshipType }),
          search: term,
        },
      });
    }, 300),
  );

  const results = hasSearched ? (data?.searchCustomers ?? []) : [];

  useEffect(() => {
    if (open) debouncedSearch.current('');
    return () => {
      debouncedSearch.current.cancel();
    };
  }, [open]);

  const handleClose = () => {
    setSelectedUser(null);
    setHasSearched(false);
    setSearchTerm('');
    onClose();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedUser(null);
    debouncedSearch.current('');
  };

  const handleSave = () => {
    if (!selectedUser?._id) return;
    onSelect(selectedUser._id);
    handleClose();
  };

  return (
    <SplitSlotDrawer open={open} title={title} onClose={handleClose}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack direction="row" gap={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Person />}
            onClick={() => {
              onSelectMyself();
              handleClose();
            }}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            {myselfLabel}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAdd />}
            onClick={() => {
              onOpenNewBuyer();
              handleClose();
            }}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            New
          </Button>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setSelectedUser(null);
            debouncedSearch.current(e.target.value);
          }}
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <Search sx={{ mr: 1, color: 'text.secondary' }} />
              ),
              endAdornment: searching ? (
                <CircularProgress size={20} />
              ) : searchTerm ? (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <Clear fontSize="small" />
                </IconButton>
              ) : null,
            },
          }}
        />

        {!selectedUser && (
          <List sx={{ mt: 1 }}>
            {results.map((u) => (
              <ListItemButton
                key={u._id}
                onClick={() => setSelectedUser(u)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText
                  primary={[u.first_name, u.last_name]
                    .filter(Boolean)
                    .join(' ')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondary={
                    u.email ||
                    (u.phone ? formatPhoneDisplay(u.phone) : u.company)
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {selectedUser && (
          <Card
            variant="outlined"
            sx={{ mt: 2, borderColor: 'primary.main', borderWidth: 2 }}
          >
            <CardContent>
              <Stack direction="row" alignItems="flex-start" gap={1.5}>
                <CheckCircle color="primary" sx={{ mt: 0.25 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {[selectedUser.first_name, selectedUser.last_name]
                      .filter(Boolean)
                      .join(' ')}
                  </Typography>
                  {selectedUser.email && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  )}
                  {selectedUser.phone && (
                    <Typography variant="body2" color="text.secondary">
                      {formatPhoneDisplay(selectedUser.phone)}
                    </Typography>
                  )}
                  {selectedUser.company && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.company}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedUser(null)}
                  sx={{ textTransform: 'none', pt: 0 }}
                >
                  Change
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>

      <Stack
        direction="row"
        gap={1}
        justifyContent="flex-end"
        sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedUser}
          onClick={handleSave}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </Stack>
    </SplitSlotDrawer>
  );
}
